import { Component, inject } from '@angular/core';
import { DataResult, ICRUDOptions } from '@phx-erp/shared';
import { lastValueFrom } from 'rxjs';
import { BankAccount, SearchInput } from '../../../../generated/generated-types';
import { GenericBaseModelDropDown } from '../../../../shared/components/genericBaseModelDropDown';
import { TenantService } from '../../../../shared/services/tenant.service';
import { BankAccountService } from '../../services/bank-account.service';
import { TenantBankAccountLinkService } from '../../services/tenant-bank-account-link.service';


@Component({
  selector: 'app-bank-account-dropdown',
  templateUrl: './bank-account-dropdown.component.html',
  standalone: true
})
export class BankAccountDropdownComponent extends GenericBaseModelDropDown<BankAccount> {

  private tenantBankAccountLinkService = inject(TenantBankAccountLinkService);
  private tenantService = inject(TenantService);

  constructor(private service: BankAccountService) {
    super(service, "BankAccount");
  }

  /**
   * Document bank accounts are the active tenant's accounts (TenantBankAccountLink) only.
   * Set `props.onlyTenantBankAccounts` on that field.
   */
  override async executeSearch(searchInput: SearchInput, options?: ICRUDOptions): Promise<DataResult<BankAccount>> {
    const onlyTenantBankAccounts = this.props?.onlyTenantBankAccounts === true
      || this.field?.props?.onlyTenantBankAccounts === true;
    if (!onlyTenantBankAccounts) {
      return super.executeSearch(searchInput, options);
    }

    if ((searchInput.skip ?? 0) > 0) {
      return { result: [], count: 0, isLastPage: true };
    }

    const tenantId = this.tenantService.getActiveTenantFromCache()?.id;
    const page = await lastValueFrom(
      this.tenantBankAccountLinkService.getItems({ take: 500, requireTotalCount: false }).single$
    );
    const term = (searchInput.term ?? '').trim().toLowerCase();
    const links = [...(page.getTenantBankAccountLinks?.items ?? [])]
      .filter(link => !link.deleted && !!link.bankAccount?.id && (!tenantId || link.tenantId === tenantId))
      .sort((a, b) => Number(b.isDefault) - Number(a.isDefault));
    const seen = new Set<string>();
    const result: BankAccount[] = [];
    for (const link of links) {
      const account = link.bankAccount;
      if (!account?.id || seen.has(account.id) || !this.matchesTerm(account, term)) {
        continue;
      }
      seen.add(account.id);
      result.push(account);
    }

    return { result, count: result.length, isLastPage: true };
  }

  private matchesTerm(account: BankAccount, term: string): boolean {
    if (!term) {
      return true;
    }
    const compactTerm = term.replace(/\s+/g, '');
    const raw = [
      account.iban,
      account.accountHolder,
      account.accountNumber,
      account.description,
      account.bank?.identifier,
      account.bank?.bic,
    ].filter((value): value is string => !!value).join(' ').toLowerCase();
    return raw.includes(term) || raw.replace(/\s+/g, '').includes(compactTerm);
  }

}
